'use client';

import React, { useState, useMemo, useCallback } from 'react';
import type { Reminder } from '@/core/domain';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { useToast } from '@/core/presentation/components/Toast';
import { AppTabsToolbar, appTabsSectionContentClassName } from '@/core/presentation/components/ui/AppTabsToolbar';
import { Tabs, TabsContent, TabsTrigger } from '@/core/presentation/components/ui/Tabs';
import { SectionScreenLayout } from '@/features/app/presentation/components/SectionScreenLayout';
import { LembretesListPanel } from '@/features/app/presentation/components/LembretesListPanel';
import { RemindersCalendar } from '@/features/app/presentation/components/RemindersCalendar';
import {
  LEMBRETES_CAL_SELECT_DAY_BELOW,
  LEMBRETES_CAL_TAB_HINT,
  LEMBRETES_CHK_ENVIADOS,
  LEMBRETES_CHK_FUTUROS,
  LEMBRETES_CLIENT_FALLBACK,
  LEMBRETES_LISTA_COMO_FUNCIONA_BODY,
  LEMBRETES_LISTA_COMO_FUNCIONA_TITLE,
  LEMBRETES_LISTA_INTRO,
  LEMBRETES_LIST_EMPTY_GLOBAL,
  LEMBRETES_LIST_LOADING,
  LEMBRETES_LIST_EMPTY_FILTERED,
  LEMBRETES_LIST_EMPTY_FUTUROS,
  LEMBRETES_LIST_EMPTY_FUTUROS_DAY,
  LEMBRETES_LIST_EMPTY_ENVIADOS,
  LEMBRETES_SECTION_SUBTITLE,
  LEMBRETES_SECTION_TITLE,
  LEMBRETES_TAB_CALENDARIO,
  LEMBRETES_TAB_LISTA,
  LEMBRETES_TABLIST_ARIA_LABEL,
} from '@/features/app/presentation/constants/lembretes-calendario-copy';
import { localTodayDateKey } from '@/features/app/presentation/utils/calendar-day-cell';
import {
  cohortForSendDay,
  isLembreteEnviado,
  normalizeSentAt,
} from '@/features/app/presentation/utils/reminder-day-metrics';
import { useReminders } from '@/features/app/reminders/presentation/context/ReminderContext';
import { useClients } from '@/features/app/clients/presentation/context/ClientContext';
import { useAwaitInitialFetch } from '@/features/app/presentation/hooks/use-await-initial-fetch';

type TabId = 'calendario' | 'lista';

function formatDate(iso: string): string {
  if (!iso) return '—';
  const dayKey = iso.trim().length >= 10 ? iso.trim().slice(0, 10) : iso.trim();
  return new Date(`${dayKey}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function isLembreteFuturo(sentAt: string[] | string | null | undefined, expectedDate: string): boolean {
  return normalizeSentAt(sentAt).length === 0 && expectedDate >= todayISO();
}

function expectedDateKey(expectedDate: string): string {
  const t = expectedDate.trim();
  return t.length >= 10 ? t.slice(0, 10) : t;
}

const TAB_CALENDARIO: TabId = 'calendario';
const TAB_LISTA: TabId = 'lista';

export const LembretesEnviadosSection: React.FC = () => {
  const toast = useToast();
  const { state: reminderState, events: reminderEvents } = useReminders();
  const { state: clientState, events: clientEvents } = useClients();
  const [activeTab, setActiveTab] = useState<TabId>(TAB_CALENDARIO);
  const [selectedSendDayKey, setSelectedSendDayKey] = useState<string | null>(null);
  const [showEnviados, setShowEnviados] = useState(true);
  const [showFuturos, setShowFuturos] = useState(true);

  const loadSection = useCallback(async () => {
    await Promise.all([reminderEvents.getByUserId(), clientEvents.getByUserId()]);
  }, [reminderEvents.getByUserId, clientEvents.getByUserId]);
  const sectionDataReady = useAwaitInitialFetch(loadSection, [loadSection]);

  const lembretesFuturos = useMemo(() => {
    return reminderState.reminders
      .filter((r) => isLembreteFuturo(r.sent_at, r.expected_date))
      .sort((a, b) => a.expected_date.localeCompare(b.expected_date));
  }, [reminderState.reminders]);

  const lembretesEnviados = useMemo(() => {
    return reminderState.reminders
      .filter((r) => isLembreteEnviado(r.sent_at))
      .sort((a, b) => {
        const aSent = normalizeSentAt(a.sent_at);
        const bSent = normalizeSentAt(b.sent_at);
        const aLast = aSent[aSent.length - 1] ?? '';
        const bLast = bSent[bSent.length - 1] ?? '';
        return bLast.localeCompare(aLast);
      });
  }, [reminderState.reminders]);

  const enviadosLista = useMemo(() => {
    return showEnviados ? lembretesEnviados : [];
  }, [showEnviados, lembretesEnviados]);

  const futurosLista = useMemo(() => {
    return showFuturos ? lembretesFuturos : [];
  }, [showFuturos, lembretesFuturos]);

  const enviadosCalendarioDia = useMemo(() => {
    if (!selectedSendDayKey) return [];
    return cohortForSendDay(lembretesEnviados, selectedSendDayKey);
  }, [selectedSendDayKey, lembretesEnviados]);

  const futurosCalendarioDia = useMemo(() => {
    if (!selectedSendDayKey) return [];
    return lembretesFuturos.filter((r) => expectedDateKey(r.expected_date) === selectedSendDayKey);
  }, [selectedSendDayKey, lembretesFuturos]);

  const emptyEnviadosCal = useMemo(
    () =>
      lembretesEnviados.length === 0 ? LEMBRETES_LIST_EMPTY_ENVIADOS : LEMBRETES_LIST_EMPTY_FILTERED,
    [lembretesEnviados.length]
  );

  const emptyFuturosCal = useMemo(
    () =>
      lembretesFuturos.length === 0 ? LEMBRETES_LIST_EMPTY_FUTUROS : LEMBRETES_LIST_EMPTY_FUTUROS_DAY,
    [lembretesFuturos.length]
  );

  const getClientName = useCallback(
    (clientId: string): string => {
      const client = clientState.clients.find((c) => c.id === clientId);
      return client?.name ?? LEMBRETES_CLIENT_FALLBACK;
    },
    [clientState.clients]
  );

  const bothTypesVisible = showEnviados && showFuturos;
  const showSectionTitlesLista = bothTypesVisible;

  const setEnviadosChecked = (checked: boolean) => {
    if (!checked && !showFuturos) return;
    setShowEnviados(checked);
  };

  const setFuturosChecked = (checked: boolean) => {
    if (!checked && !showEnviados) return;
    setShowFuturos(checked);
  };

  const hasAnyReminderInAccount = reminderState.reminders.length > 0;

  const onUpdateReminder = useCallback(
    async (id: string, partial: Partial<Reminder>) => reminderEvents.update(id, partial),
    [reminderEvents.update]
  );

  const onActionSuccess = useCallback(
    (message: string) => {
      toast.success(message);
    },
    [toast]
  );

  const onActionError = useCallback(
    (message: string) => {
      toast.error(message);
    },
    [toast]
  );

  const calendarBelowLegend = useMemo(() => {
    if (!selectedSendDayKey) {
      return <p className="text-sm text-text-muted leading-relaxed pt-1">{LEMBRETES_CAL_SELECT_DAY_BELOW}</p>;
    }
    const today = localTodayDateKey();
    const showRecebidosForDay = selectedSendDayKey <= today;
    const showFuturosForDay = selectedSendDayKey >= today;

    return (
      <div className="space-y-3 pt-2 min-w-0">
        <p className="text-sm font-medium text-navy-dark">{formatDate(selectedSendDayKey)}</p>
        <LembretesListPanel
          enviados={enviadosCalendarioDia}
          futuros={futurosCalendarioDia}
          showEnviadosSection={showRecebidosForDay}
          showFuturosSection={showFuturosForDay}
          showSectionTitles
          getClientName={getClientName}
          emptyEnviadosMessage={emptyEnviadosCal}
          emptyFuturosMessage={emptyFuturosCal}
          onUpdateReminder={onUpdateReminder}
          onActionSuccess={onActionSuccess}
          onActionError={onActionError}
        />
      </div>
    );
  }, [
    selectedSendDayKey,
    enviadosCalendarioDia,
    futurosCalendarioDia,
    getClientName,
    emptyEnviadosCal,
    emptyFuturosCal,
    onUpdateReminder,
    onActionSuccess,
    onActionError,
  ]);

  return (
    <SectionScreenLayout title={LEMBRETES_SECTION_TITLE} subtitle={LEMBRETES_SECTION_SUBTITLE}>
      {!sectionDataReady ? (
        <p className="text-text-muted text-sm py-6">{LEMBRETES_LIST_LOADING}</p>
      ) : (
        <div className="space-y-4 min-w-0 overflow-hidden">
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as TabId)}
            className="w-full min-w-0"
          >
            <AppTabsToolbar listAriaLabel={LEMBRETES_TABLIST_ARIA_LABEL}>
              <TabsTrigger value={TAB_CALENDARIO}>{LEMBRETES_TAB_CALENDARIO}</TabsTrigger>
              <TabsTrigger value={TAB_LISTA}>{LEMBRETES_TAB_LISTA}</TabsTrigger>
            </AppTabsToolbar>

            <TabsContent
              value={TAB_CALENDARIO}
              className={appTabsSectionContentClassName('space-y-4 overflow-hidden')}
            >
              <p className="text-sm text-text-muted leading-relaxed">{LEMBRETES_CAL_TAB_HINT}</p>
              <RemindersCalendar
                reminders={lembretesEnviados}
                futureReminders={lembretesFuturos}
                selectedDayKey={selectedSendDayKey}
                onSelectDay={setSelectedSendDayKey}
                belowLegend={calendarBelowLegend}
              />
            </TabsContent>

            <TabsContent
              value={TAB_LISTA}
              className={appTabsSectionContentClassName('space-y-4 overflow-hidden')}
            >
              <p className="text-sm text-text-muted leading-relaxed">{LEMBRETES_LISTA_INTRO}</p>

              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-6 rounded-lg border border-border-light bg-white px-4 py-3">
                <label className="flex items-center gap-2.5 cursor-pointer text-sm text-navy-dark font-medium">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border-light text-cyan focus:ring-cyan"
                    checked={showEnviados}
                    onChange={(e) => setEnviadosChecked(e.target.checked)}
                  />
                  {LEMBRETES_CHK_ENVIADOS}
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer text-sm text-navy-dark font-medium">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border-light text-cyan focus:ring-cyan"
                    checked={showFuturos}
                    onChange={(e) => setFuturosChecked(e.target.checked)}
                  />
                  {LEMBRETES_CHK_FUTUROS}
                </label>
              </div>

              <div className="rounded-lg border border-cyan/30 bg-cyan/5 px-4 py-3 space-y-2">
                <p className="text-sm font-medium text-navy-dark flex items-center gap-2">
                  <AppIcon name="InformationCircleIcon" size={18} className="text-cyan shrink-0" />
                  {LEMBRETES_LISTA_COMO_FUNCIONA_TITLE}
                </p>
                <p className="text-sm text-text-muted leading-relaxed">{LEMBRETES_LISTA_COMO_FUNCIONA_BODY}</p>
              </div>

              {reminderState.error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {reminderState.error}
                </div>
              )}

              {!hasAnyReminderInAccount ? (
                <div className="rounded-xl border border-border-light bg-[#fafbfc] p-8 text-center">
                  <AppIcon name="BellAlertIcon" size={40} className="mx-auto text-text-muted mb-3" />
                  <p className="text-text-muted text-sm">{LEMBRETES_LIST_EMPTY_GLOBAL}</p>
                </div>
              ) : (
                <LembretesListPanel
                  enviados={enviadosLista}
                  futuros={futurosLista}
                  showEnviadosSection={showEnviados}
                  showFuturosSection={showFuturos}
                  showSectionTitles={showSectionTitlesLista}
                  getClientName={getClientName}
                  emptyEnviadosMessage={LEMBRETES_LIST_EMPTY_ENVIADOS}
                  emptyFuturosMessage={LEMBRETES_LIST_EMPTY_FUTUROS}
                  onUpdateReminder={onUpdateReminder}
                  onActionSuccess={onActionSuccess}
                  onActionError={onActionError}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </SectionScreenLayout>
  );
};
